import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Lead } from 'model/leads.model';
import { LeadsService } from 'service/leads.service';

type DraftOption = { Name: string; Id: string };

export type AddLeadDraft = {
  formValues: Record<string, string>;
  selectedItems: {
    assignTo: DraftOption[];
    address: DraftOption[];
  };
  selectedIntegrations: {
    captiv8: DraftOption[];
    demographicPro: DraftOption[];
  };
  activeStep: number;
  completed: { [k: number]: boolean };
};
interface LeadsState {
  leads: Lead[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  limit: number;
  agencyDropdown: string[];
  assigneeDropdown: { Id: string; Name: string }[];
  addLeadDraft: AddLeadDraft | null;
  openAddLeadDrawer: boolean;
  demographicData?: any;
  editLead: Lead | null;
  isEditMode: boolean;
}

const initialState: LeadsState = {
  leads: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  limit: 10,
  agencyDropdown: [],
  assigneeDropdown: [],
  addLeadDraft: null,
  openAddLeadDrawer: false,
  demographicData: undefined,
  editLead: null,
  isEditMode: false,
};

export const getAgencyDropdown = createAsyncThunk(
  'leads/getAgencyDropdown',
  async (_, { rejectWithValue }) => {
    try {
      const response = await LeadsService.getAgencyDropdowns();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch agencies');
    }
  },
);
export const getAssigneeDropdown = createAsyncThunk(
  'leads/getAssigneeDropdown',
  async (_, { rejectWithValue }) => {
    try {
      const response = await LeadsService.getAssigneeDropdowns();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assignees');
    }
  },
);

export const getAllLeads = createAsyncThunk(
  'leads/getAllLeads',
  async (
    params: {
      page: number;
      limit: number;
      mine?: boolean;
      search?: string;
      sport?: string;
      sort?: string;
      lastModifiedFrom?: string;
      lastModifiedTo?: string;
      lastModifiedDate?: string;
      assignedTo?: string;
      agencyName?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await LeadsService.getAllLeads(
        params.page,
        params.limit,
        params.mine,
        params.search,
        params.sport,
        params.sort,
        params.lastModifiedFrom,
        params.lastModifiedTo,
        params.lastModifiedDate,
        params.assignedTo,
        params.agencyName,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leads');
    }
  },
);

export const updateLead = createAsyncThunk(
  'leads/updateLead',
  async (
    params: {
      leadId: string;
      data: any;
      page: number;
      limit: number;
      mine?: boolean;
      search?: string;
      sport?: string;
      sort?: string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await LeadsService.updateLead(params.leadId, params.data);
      // Fetch all leads again after update
      await dispatch(
        getAllLeads({
          page: params.page,
          limit: params.limit,
          mine: params.mine,
          search: params.search,
          sport: params.sport,
          sort: params.sort,
        }),
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update lead');
    }
  },
);

export const deleteLead = createAsyncThunk(
  'leads/deleteLead',
  async (
    params: {
      leadId: string;
      page: number;
      limit: number;
      mine?: boolean;
      search?: string;
      sport?: string;
      sort?: string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await LeadsService.deleteLead(params.leadId);
      // Fetch all leads again after delete
      await dispatch(
        getAllLeads({
          page: params.page,
          limit: params.limit,
          mine: params.mine,
          search: params.search,
          sport: params.sport,
          sort: params.sort,
        }),
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete lead');
    }
  },
);

export const fetchDemographicProData = createAsyncThunk(
  'leads/fetchDemographicProData',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await LeadsService.getDemographicProData(id);

      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch demographic pro data',
      );
    }
  },
);
const leadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    clearLeads: (state) => {
      state.leads = [];
      state.error = null;
    },
    setAddLeadDraft: (state, action: PayloadAction<AddLeadDraft | null>) => {
      state.addLeadDraft = action?.payload;
    },
    clearAddLeadDraft: (state) => {
      state.addLeadDraft = null;
    },
    setOpenAddLeadDrawer: (state, action: PayloadAction<boolean>) => {
      state.openAddLeadDrawer = action?.payload;
    },
    setEditLeadData: (state, action: PayloadAction<Lead | null>) => {
      state.editLead = action.payload;
      state.isEditMode = Boolean(action.payload);
      state.openAddLeadDrawer = Boolean(action.payload);
    },
    clearEditLeadData: (state) => {
      state.editLead = null;
      state.isEditMode = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action?.payload?.data || [];
        state.totalCount = action?.payload?.total || 0;
        state.currentPage = action?.payload?.page || 1;
        state.limit = action?.payload?.limit || 10;
      })
      .addCase(getAllLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload as string;
      })
      .addCase(updateLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.loading = false;
        // Leads will be refetched, so we don't need to update here
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload as string;
      })
      .addCase(deleteLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.loading = false;
        // Leads will be refetched, so we don't need to update here
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload as string;
      })

      .addCase(getAgencyDropdown.fulfilled, (state, action) => {
        state.agencyDropdown = action?.payload?.leads_filter_values?.agencyName || [];
      })
      .addCase(getAgencyDropdown.rejected, (state, action) => {
        state.error = action?.payload as string;
      })
      .addCase(getAssigneeDropdown.fulfilled, (state, action) => {
        state.assigneeDropdown = action.payload.map((user: any) => ({
          ...user,
          Name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        }));
      })
      .addCase(getAssigneeDropdown.rejected, (state, action) => {
        state.error = action?.payload as string;
      })
      .addCase(fetchDemographicProData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDemographicProData.fulfilled, (state, action) => {
        state.loading = false;
        state.demographicData = action?.payload;
        // Handle demographic pro data as needed
      })
      .addCase(fetchDemographicProData.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload as string;
      });
  },
});

export const {
  clearLeads,
  setAddLeadDraft,
  clearAddLeadDraft,
  setOpenAddLeadDrawer,
  setEditLeadData,
  clearEditLeadData,
} = leadsSlice.actions;
export default leadsSlice.reducer;
